import { memo, useCallback, useState } from 'react';

import { WorkspaceAgentStepperControl } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/form/WorkspaceAgentStepperControl';
import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { useTrackWorkspaceAgentFormChoice } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useTrackWorkspaceAgentFormChoice';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type StepperElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type PrivateWorkspaceAgentPanelStepperDockProps = {
  elicitation: StepperElicitation;
};

export const PrivateWorkspaceAgentPanelStepperDock = memo(
  ({ elicitation }: PrivateWorkspaceAgentPanelStepperDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const trackChoice = useTrackWorkspaceAgentFormChoice(elicitation);
    const { max, min, step, value: initial } = elicitation.payload;
    const [value, setValue] = useState(initial);

    const handleContinue = useCallback(() => {
      trackChoice({ cta: 'approve' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        numberValue: value,
        response: 'approved',
      });
    }, [elicitation.id, session, trackChoice, value]);

    const handleCancel = useCallback(() => {
      trackChoice({ cta: 'cancel' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'denied',
      });
    }, [elicitation.id, session, trackChoice]);

    return (
      <WorkspaceAgentPanelConsentDock
        title={<PrivateWorkspaceAgentMonikerText value={elicitation.payload.prompt} />}
        approveLabel="Continue"
        data-testid="WORKSPACE_AGENT_STEPPER"
        onApprove={handleContinue}
        onCancel={handleCancel}
      >
        <WorkspaceAgentStepperControl max={max} min={min} onChange={setValue} step={step} value={value} />
      </WorkspaceAgentPanelConsentDock>
    );
  },
);

PrivateWorkspaceAgentPanelStepperDock.displayName = 'PrivateWorkspaceAgentPanelStepperDock';
